import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCog } from '@fortawesome/free-solid-svg-icons'
import { faSmile } from '@fortawesome/free-regular-svg-icons'
import 'emoji-picker-element'

import { getChannelClient } from 'src/utils/messages/singleton'
import InputField     from 'src/components/InputField'
import useValidation  from './useValidation'

const maxLength = 150

const insertTextAtCursor = (element, text) => {
  const selectionStart = element.selectionStart
  const selectionEnd = element.selectionEnd
  element.value = element.value.substring(0, selectionStart)
  + text + element.value.substring(selectionEnd)
  element.selectionStart = selectionStart + text.length
  element.selectionEnd = selectionStart + text.length
}

const Comment = ({ i18n, donationsPath, donation }) => {
  const [ status, setStatus ] = useState('')
  const [ comment, setComment ] = useState('')
  const validation = useValidation({
    i18n,
    fieldSet: { comment: { required: true, maxLength } },
  })

  const button = useRef()
  const textarea = useRef()
  const emojiPicker = useRef()

  const handleChange = e => {
    setComment(e.target.value)
    validation.resetErrorsFor('comment')
  }

  const submitComment = () => {
    if (status && status != 'failed') return

    const url = [ donationsPath, donation.token, 'comment' ].join('/')

    setStatus('processing')

    fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ comment })
    }).then(response => response.json().then(data => {
      if (response.ok) {
        setStatus('sent')
        getChannelClient().send('commentAdded', data)
      }
      else
        setStatus('failed')
    })).catch(error => {
      setStatus('failed')
      console.error(error)
    })
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (validation.validate({ comment }))
      submitComment()
  }

  const error = validation.errors?.comment?.[0]

  useEffect(() => {
    const handleEmojiClick = e => {
      // Simple insertion at end doesn't cut it
      // setComment(comment => comment + e.detail.unicode)
      insertTextAtCursor(textarea.current, e.detail.unicode)
      setComment(textarea.current.value)

      // I have tried refactoring this but it's too fussy to work any other way
      const modal = document.getElementById('emojiModal')
      const handleModalHidden = () => {
        textarea.current.focus()
        modal.removeEventListener('hidden.bs.modal', handleModalHidden)
      }
      modal.addEventListener('hidden.bs.modal', handleModalHidden)
      bootstrap.Modal.getOrCreateInstance(modal).hide()
    }

    emojiPicker.current.addEventListener('emoji-click', handleEmojiClick)

    return () => {
      emojiPicker.current?.removeEventListener('emoji-click', handleEmojiClick)
    }
  }, [])

  return (
    <form onSubmit={handleSubmit} noValidate>
      {
        status == 'sent'
      ? <div className="alert alert-success">{i18n.t('comment.sent')}</div>
      : <InputField
          id="comment"
          label={i18n.t('comment.label')}
          error={error}
          wrapperClass="mb-3 text-start"
          hint={
            <span>
              <span className={comment.length > maxLength ? 'text-danger' : ''}>
                {comment.length}
              </span>/{maxLength} characters
            </span>
          }
        >
          <div className={[
            'position-relative',
            error && 'is-invalid',
          ].filter(Boolean).join(' ')}>
            <textarea
              ref={textarea}
              id="comment"
              className={[
                'form-control',
                error && 'is-invalid',
              ].filter(Boolean).join(' ')}
              placeholder={i18n.t('comment.placeholder')}
              rows="3"
              value={comment}
              onChange={handleChange}
              style={{
                paddingRight: `calc(${button.current?.offsetWidth || 0}px + 0.75rem + 0.5rem)`,
              }}
            />
            <div className="position-absolute top-0 end-0">
              <button
                type="button"
                className="btn btn-link link-dark text-decoration-none py-1 px-2"
                title="Emoji"
                data-bs-toggle="modal"
                data-bs-target="#emojiModal"
              >
                <FontAwesomeIcon icon={faSmile} />
              </button>
              {createPortal(
                <emoji-picker ref={emojiPicker} class="light rounded-2" />
                , document.getElementById('emojiModalBody')
              )}
            </div>
            <button
              ref={button}
              className="btn btn-sm btn-primary position-absolute bottom-0 end-0 m-2"
              disabled={status == 'processing'}>
              {(() => {
                switch (status) {
                  case 'failed':
                    return i18n.t('ajax_error')
                  case 'processing':
                    return (
                      <span>
                        <FontAwesomeIcon icon={faCog} spin className="me-2" />
                        {i18n.t('submitting')}
                      </span>
                    )
                  default:
                    return i18n.t('submit')
                }
              })()}
            </button>
          </div>
        </InputField>
      }
    </form>
  )
}

export default Comment
